import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { site } from "@/content/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CoverTile } from "@/components/CoverTile";
import { StreamRow } from "@/components/StreamRow";

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

function Hero() {
  const heroRef = useRef<HTMLElement | null>(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={heroRef}
      data-testid="home-hero"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div
        style={reduce ? undefined : { y: bgY }}
        className="relative h-[58vh] w-full shrink-0 overflow-hidden lg:absolute lg:inset-0 lg:h-full lg:scale-[1.08]"
      >
        {photoFailed ? (
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_18%_8%,rgba(0,217,255,0.15),transparent_52%),radial-gradient(ellipse_at_82%_28%,rgba(123,44,255,0.18),transparent_55%),radial-gradient(ellipse_at_50%_95%,rgba(255,45,154,0.12),transparent_52%)]" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
                backgroundSize: "76px 76px",
              }}
            />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap font-display text-[26vw] uppercase leading-none text-white/[0.04]">
              EVE
            </span>
          </div>
        ) : (
          <img
            src={site.photos.hero.path}
            alt={site.photos.hero.alt}
            onError={() => setPhotoFailed(true)}
            fetchPriority="high"
            className="h-full w-full object-cover object-[center_20%] lg:w-[118%] lg:max-w-none lg:object-[center_22%]"
          />
        )}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,5,7,0.2)_0%,rgba(5,5,7,0.7)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-eve-bg lg:h-[64%] lg:bg-gradient-to-t lg:from-eve-bg lg:via-eve-bg/70 lg:to-transparent" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-t from-transparent to-eve-bg/60" />
      </motion.div>

      <motion.div
        style={reduce ? undefined : { opacity: fade }}
        className="container-eve relative z-10 flex flex-col items-start pb-12 pt-9 text-left lg:pb-16 lg:pt-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="eyebrow mb-8"
        >
          EVE — Modern Rock · Indonesia
        </motion.p>

        <h1 className="font-display uppercase leading-[0.92] tracking-tight">
          {site.band.headlineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block text-[clamp(2.6rem,5.4vw,4.5rem)] ${
                  i === 1 ? "gradient-text" : "text-eve-ink"
                }`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay: 0.2 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8 text-base text-eve-silver sm:text-lg"
        >
          {site.band.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-12 flex w-full max-w-sm flex-col items-stretch gap-4 sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
        >
          <Link to="/music" data-testid="hero-listen-now-btn" className="btn-primary w-full sm:w-auto">
            Listen now <ArrowRight size={14} />
          </Link>
          <a
            href={site.links.universe}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-join-universe-btn"
            className="btn-outline w-full sm:w-auto"
          >
            Join the Universe <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-10 right-8 z-10 hidden md:block"
        aria-hidden="true"
      >
        <div className="flex flex-col items-center gap-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-white/40">Scroll</span>
          <div className="h-10 w-px overflow-hidden bg-white/15">
            <motion.div
              className="h-1/2 w-full bg-eve-cyan"
              animate={reduce ? undefined : { y: [-20, 40] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function ListenNow() {
  const release = site.releases.find((r) => r.id === site.featuredReleaseId)!;
  return (
    <section data-testid="home-listen-section" className="py-24 lg:py-32">
      <div className="container-eve grid items-center gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
        <Reveal>
          <CoverTile release={release} testId="home-featured-cover" />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow mb-5">Latest Release</p>
          <h2 className="font-display text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl">
            Listen now<span className="text-eve-cyan">.</span>
          </h2>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="font-display text-2xl uppercase tracking-wide sm:text-3xl">
              {release.title}
            </span>
            <span className="chip border-eve-cyan/40 text-eve-cyan">New</span>
          </div>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-eve-silver">
            {release.type} · {release.date}
          </p>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-eve-silver">
            {release.description}
          </p>
          <div className="mt-9">
            <StreamRow platforms={release.platforms} testPrefix="home-featured" />
          </div>
          <div className="mt-10">
            <Link to="/music" data-testid="home-all-music-btn" className="btn-outline">
              Explore all music <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function JoinUniverse() {
  return (
    <section
      data-testid="home-evecore-section"
      className="relative overflow-hidden border-t border-white/10 py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,rgba(123,44,255,0.18),transparent_60%)]" />
      <div className="container-eve relative flex flex-col items-center text-center">
        <Reveal>
          <p className="eyebrow mb-5">The Fan Community</p>
          <h2 className="font-display text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl">
            Join {site.band.community} Universe
          </h2>
          <p className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-eve-silver sm:text-lg">
            {site.band.community} is the official fan community of {site.band.name}. Connect with
            fellow fans, get closer to the band, and join the journey from the first riff to the
            biggest stage.
          </p>
          <a
            href={site.links.universe}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-join-universe-btn"
            className="btn-primary mt-10"
          >
            Join the Universe <ArrowUpRight size={14} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="EVE Official Website | Modern Rock Band Indonesia"
        description="Discover EVE, a modern rock band from Surabaya, Indonesia. Listen to our music, watch live performances, explore upcoming shows, and join the EVECORE community."
      />
      <Hero />
      <Marquee
        items={[
          "The Ultimate Rock Party Experience",
          "Feel the Energy",
          "Break Your Limit",
          site.band.community,
          "EVE — Modern Rock, Indonesia",
        ]}
      />
      <ListenNow />
      <JoinUniverse />
    </>
  );
}
